import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  User, MapPin, Package, ShoppingCart, Bot, Sparkles,
  Calculator, DollarSign, TrendingUp, CheckSquare, Square,
  ArrowRight, ArrowLeft, Eye, Store, Truck, Star, Filter,
  Search, X, Check, Zap, Clock, Wallet, BarChart3, Play,
  CreditCard, Plus, Minus, History, AlertCircle, ChevronRight,
  ChevronDown, Settings, LogOut, Edit, Phone, Mail, Percent,
  MessageCircle, Image as ImageIcon, Layers, Building2, Banknote,
  ArrowUpRight, ArrowDownLeft, Copy, QrCode, Loader2, Bell, RefreshCw,
  ExternalLink
} from "lucide-react";
import { carteiraService, fornecedorService, perfilService, pixService } from "../../services/api";
import { NotificationToggle, NotificationPrompt } from "./NotificationToggle";
import authService from "../../services/authService";
import AvisoAutomacao24h from "../AvisoAutomacao24h";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// ACHADO DA REVISÃO: antes era um ID fixo/simulado ("afiliado-demo-001"),
// que nunca correspondia ao usuário realmente logado - com o backend
// agora exigindo que o dono do recurso bata com o token (assert_owner_or_admin),
// isso fazia toda chamada de carteira/perfil retornar 403. Agora usa o
// ID real de quem está logado.
const USUARIO_ID = authService.getUser()?.id || "";
const PAPEL_USUARIO = authService.getUser()?.role || "";
const EH_ADMIN = ["super_admin", "admin_operacional", "admin_financeiro", "admin"].includes(PAPEL_USUARIO);

const Afiliado = ({ setCurrentSection }) => {
  // Estados principais
  const [activeTab, setActiveTab] = useState('inicio'); // inicio, fornecedores, catalogo, calculadora, tendencias, perfil
  const [showCarteiraModal, setShowCarteiraModal] = useState(false);
  const [showPerfilModal, setShowPerfilModal] = useState(false);
  const [selectedEstado, setSelectedEstado] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFornecedor, setSelectedFornecedor] = useState(null);
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [selectedSubcategoria, setSelectedSubcategoria] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedMarketplaces, setSelectedMarketplaces] = useState({});
  const [postingProgress, setPostingProgress] = useState(null);
  const [postingComplete, setPostingComplete] = useState(false);
  const [margemPadrao, setMargemPadrao] = useState(30);
  const [loading, setLoading] = useState(false);
  const [fornecedoresAPI, setFornecedoresAPI] = useState([]);
  const [estadosComFornecedores, setEstadosComFornecedores] = useState([]);
  
  // Estados da Carteira Digital
  const [carteiraTab, setCarteiraTab] = useState('resumo'); // resumo, depositar
  const [valorDeposito, setValorDeposito] = useState('');
  const [carteiraLoading, setCarteiraLoading] = useState(false);
  
  // Estados do PIX
  const [pixData, setPixData] = useState(null); // Dados do QR Code PIX gerado
  const [pixLoading, setPixLoading] = useState(false);
  const [showPixModal, setShowPixModal] = useState(false);

  // Dados do Afiliado
  const [afiliadoData, setAfiliadoData] = useState({
    nome: "",
    email: "",
    telefone: "",
    foto: null,
    endereco: "",
    margemPadrao: 30,
    // Carteira Digital
    saldoDisponivel: 0,
    saldoBloqueado: 0,
    lucroDisponivel: 0,
    // Estatísticas (carregadas de verdade em carregarPerfilEDashboard)
    lucroHoje: 0,
    lucroMes: 0,
    produtosAtivos: 0,
    vendasMes: 0
  });

  // Histórico da Carteira
  const [historicoCarteira, setHistoricoCarteira] = useState([]);

  // =====================
  // EFEITOS - CARREGAR DADOS DA API
  // =====================
  
  // Carregar carteira ao montar e ao abrir o modal
  useEffect(() => {
    carregarCarteira();
    carregarPerfilEDashboard();
  }, []);

  useEffect(() => {
    if (showCarteiraModal) {
      carregarCarteira();
    }
  }, [showCarteiraModal]);

  // Carregar estados com fornecedores ao montar
  useEffect(() => {
    carregarEstados();
  }, []);

  // Carregar fornecedores quando selecionar estado
  useEffect(() => {
    if (activeTab === 'fornecedores') {
      carregarFornecedores();
    }
  }, [activeTab, selectedEstado, searchTerm]);

  // ACHADO DA REVISÃO (sessão de deploy): antes, "nome", "email",
  // "lucroHoje", "lucroMes", "produtosAtivos" e "vendasMes" eram valores
  // fixos de exemplo, escritos direto no código (nunca eram substituídos
  // por dado real). Esta função busca os dados de verdade no endpoint de
  // dashboard do afiliado, que já existia no backend mas nunca era
  // chamado por esta tela.
  // ACHADO DA VARREDURA: o botao "Salvar Alteracoes" do perfil nao tinha
  // nenhuma acao - o servico perfilService.salvarPerfilAfiliado ja
  // existia e o endpoint no backend ja funcionava, so nunca eram
  // chamados pelo botao.
  const [salvandoPerfil, setSalvandoPerfil] = useState(false);
  // ACHADO DA VARREDURA: o botao "Alterar Foto" nao tinha acao nenhuma
  // - o campo "foto" ja existia no estado, e o backend ja tinha o campo
  // "avatar" reservado, so faltava o endpoint de upload em si (que
  // acabei de construir em perfil_routes.py).
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const inputFotoRef = React.useRef(null);

  const selecionarFoto = () => {
    inputFotoRef.current?.click();
  };

  const enviarFoto = async (evento) => {
    const arquivo = evento.target.files?.[0];
    if (!arquivo) return;

    setEnviandoFoto(true);
    try {
      const formData = new FormData();
      formData.append("file", arquivo);
      const resp = await authService.fetchAuth(
        `${process.env.REACT_APP_BACKEND_URL}/api/perfil/foto/${USUARIO_ID}`,
        { method: "POST", body: formData }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao enviar foto");

      setAfiliadoData((prev) => ({ ...prev, foto: `${process.env.REACT_APP_BACKEND_URL}${data.avatar_url}` }));
      toast.success("Foto de perfil atualizada!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setEnviandoFoto(false);
    }
  };

  const salvarPerfil = async () => {
    setSalvandoPerfil(true);
    try {
      await perfilService.salvarPerfilAfiliado(USUARIO_ID, {
        nome: afiliadoData.nome,
        email: afiliadoData.email,
        telefone: afiliadoData.telefone,
        endereco: afiliadoData.endereco,
        margem_padrao: margemPadrao
      });
      toast.success("Perfil salvo com sucesso!");
    } catch (error) {
      toast.error(error.message || "Erro ao salvar perfil");
    } finally {
      setSalvandoPerfil(false);
    }
  };

  const carregarPerfilEDashboard = async () => {
    try {
      const resp = await authService.fetchAuth(
        `${BACKEND_URL}/api/business/afiliado/dashboard/${USUARIO_ID}`
      );
      if (!resp.ok) return;
      const dados = await resp.json();

      setAfiliadoData(prev => ({
        ...prev,
        nome: dados.afiliado?.nome || prev.nome,
        email: dados.afiliado?.email || prev.email,
        telefone: dados.afiliado?.telefone || prev.telefone,
        lucroHoje: dados.kpis?.lucro_hoje || 0,
        lucroMes: dados.kpis?.lucro_mes || 0,
        produtosAtivos: dados.kpis?.produtos_vinculados || 0,
        vendasMes: dados.kpis?.vendas_mes || 0
      }));
    } catch (error) {
      console.error('Erro ao carregar dashboard do afiliado:', error);
    }
  };

  // Função para carregar carteira da API
  const carregarCarteira = async () => {
    try {
      setCarteiraLoading(true);
      const carteira = await carteiraService.getCarteira(USUARIO_ID, 'afiliado');
      const historico = await carteiraService.getHistorico(USUARIO_ID, 'afiliado');
      
      setAfiliadoData(prev => ({
        ...prev,
        saldoDisponivel: carteira.saldo_disponivel || 0,
        saldoBloqueado: carteira.saldo_bloqueado || 0,
        lucroDisponivel: carteira.lucro_disponivel || 0
      }));
      
      // Formatar histórico
      const historicoFormatado = historico.map(h => ({
        id: h.id,
        tipo: h.tipo,
        valor: h.valor,
        descricao: h.descricao,
        data: new Date(h.created_at).toLocaleString('pt-BR')
      }));
      setHistoricoCarteira(historicoFormatado);
      
    } catch (error) {
      console.error('Erro ao carregar carteira:', error);
      // ACHADO DA SESSÃO DE DEPLOY: antes, se a chamada falhasse por
      // qualquer instabilidade de rede/servidor, a tela mostrava valores
      // de mentira (R$ 1.250,00 de saldo) como se fossem reais - grave
      // numa tela financeira, pode confundir o afiliado sobre quanto ele
      // realmente tem disponível. Agora avisa honestamente que não
      // conseguiu carregar, sem inventar número nenhum.
      toast.error('Não foi possível carregar os dados da carteira. Tente atualizar a página.');
    } finally {
      setCarteiraLoading(false);
    }
  };

  // Função para carregar estados com fornecedores
  const carregarEstados = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/estados`);
      if (response.ok) {
        const data = await response.json();
        setEstadosComFornecedores(data);
      }
    } catch (error) {
      console.error('Erro ao carregar estados:', error);
    }
  };

  // Função para carregar fornecedores da API
  const carregarFornecedores = async () => {
    try {
      setLoading(true);
      const data = await fornecedorService.buscarFornecedores(
        selectedEstado || null,
        null,
        searchTerm || null
      );
      
      // Mapear para formato esperado pelo componente
      const fornecedoresMapeados = data.map(f => ({
        id: f.id,
        nome: f.nome_fantasia,
        cidade: f.cidade,
        estado: f.estado,
        categoria: f.categoria,
        produtos: f.total_produtos,
        rating: f.rating,
        entregas: f.entregas,
        logo: f.logo
      }));
      
      setFornecedoresAPI(fornecedoresMapeados);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
    } finally {
      setLoading(false);
    }
  };

  // Estados do Brasil (para seleção)
  const estados = [
    { uf: 'AC', nome: 'Acre' }, { uf: 'AL', nome: 'Alagoas' }, { uf: 'AP', nome: 'Amapá' },
    { uf: 'AM', nome: 'Amazonas' }, { uf: 'BA', nome: 'Bahia' }, { uf: 'CE', nome: 'Ceará' },
    { uf: 'DF', nome: 'Distrito Federal' }, { uf: 'ES', nome: 'Espírito Santo' }, { uf: 'GO', nome: 'Goiás' },
    { uf: 'MA', nome: 'Maranhão' }, { uf: 'MT', nome: 'Mato Grosso' }, { uf: 'MS', nome: 'Mato Grosso do Sul' },
    { uf: 'MG', nome: 'Minas Gerais' }, { uf: 'PA', nome: 'Pará' }, { uf: 'PB', nome: 'Paraíba' },
    { uf: 'PR', nome: 'Paraná' }, { uf: 'PE', nome: 'Pernambuco' }, { uf: 'PI', nome: 'Piauí' },
    { uf: 'RJ', nome: 'Rio de Janeiro' }, { uf: 'RN', nome: 'Rio Grande do Norte' }, { uf: 'RS', nome: 'Rio Grande do Sul' },
    { uf: 'RO', nome: 'Rondônia' }, { uf: 'RR', nome: 'Roraima' }, { uf: 'SC', nome: 'Santa Catarina' },
    { uf: 'SP', nome: 'São Paulo' }, { uf: 'SE', nome: 'Sergipe' }, { uf: 'TO', nome: 'Tocantins' }
  ];

  // Fornecedores - usar da API ou fallback
  const fornecedoresFiltrados = fornecedoresAPI.length > 0 ? fornecedoresAPI : [
    { id: 1, nome: "TechStore Ltda", cidade: "São Paulo", estado: "SP", categoria: "Tecnologia", produtos: 156, rating: 4.8, entregas: "2-5 dias", logo: "🖥️" },
    { id: 2, nome: "FashionHouse", cidade: "Rio de Janeiro", estado: "RJ", categoria: "Moda", produtos: 423, rating: 4.6, entregas: "3-7 dias", logo: "👗" },
    { id: 3, nome: "Casa & Conforto", cidade: "Belo Horizonte", estado: "MG", categoria: "Casa", produtos: 89, rating: 4.9, entregas: "4-8 dias", logo: "🏠" },
  ].filter(f => 
    (!selectedEstado || f.estado === selectedEstado) &&
    (!searchTerm || f.nome.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Categorias e Subcategorias (estilo Mercado Livre)
  const categoriasCatalogo = [
    {
      id: 1, nome: "Tecnologia", icone: "🖥️",
      subcategorias: [
        { id: 11, nome: "Celulares e Telefones", items: ["Smartphones", "Acessórios para Celular", "Capas e Películas"] },
        { id: 12, nome: "Informática", items: ["Notebooks", "Computadores", "Periféricos", "Componentes"] },
        { id: 13, nome: "Áudio e Vídeo", items: ["Fones de Ouvido", "Caixas de Som", "TVs", "Home Theater"] },
        { id: 14, nome: "Games", items: ["Consoles", "Jogos", "Acessórios Gamer"] },
      ]
    },
    {
      id: 2, nome: "Moda", icone: "👗",
      subcategorias: [
        { id: 21, nome: "Moda Masculina", items: ["Camisetas", "Calças", "Tênis", "Acessórios"] },
        { id: 22, nome: "Moda Feminina", items: ["Vestidos", "Blusas", "Calçados", "Bolsas"] },
        { id: 23, nome: "Moda Infantil", items: ["Roupas", "Calçados", "Acessórios"] },
      ]
    },
    {
      id: 3, nome: "Casa e Decoração", icone: "🏠",
      subcategorias: [
        { id: 31, nome: "Móveis", items: ["Sofás", "Mesas", "Cadeiras", "Armários"] },
        { id: 32, nome: "Decoração", items: ["Quadros", "Vasos", "Iluminação", "Tapetes"] },
        { id: 33, nome: "Cozinha", items: ["Eletrodomésticos", "Utensílios", "Panelas"] },
      ]
    },
  ];

  // Produtos do Catálogo
  const produtosCatalogo = [
    { id: 1, nome: "Notebook Gamer RTX 4060", sku: "NB-RTX4060", custoFornecedor: 4200.00, estoque: 45, categoria: "Informática", subcategoria: "Notebooks", imagem: "https://images.unsplash.com/photo-1603302576837-37561b2e2302?w=200" },
    { id: 2, nome: "Smart TV 55\" QLED 4K", sku: "TV-QLED55", custoFornecedor: 2500.00, estoque: 23, categoria: "Áudio e Vídeo", subcategoria: "TVs", imagem: "https://images.unsplash.com/photo-1593359677879-a4bb92f829d1?w=200" },
    { id: 3, nome: "Smartphone 5G 256GB", sku: "SP-5G256", custoFornecedor: 2100.00, estoque: 67, categoria: "Celulares e Telefones", subcategoria: "Smartphones", imagem: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=200" },
    { id: 4, nome: "Fone Bluetooth Premium", sku: "FN-BT-PRO", custoFornecedor: 450.00, estoque: 120, categoria: "Áudio e Vídeo", subcategoria: "Fones de Ouvido", imagem: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=200" },
    { id: 5, nome: "Smartwatch Fitness Pro", sku: "SW-FIT-PRO", custoFornecedor: 680.00, estoque: 89, categoria: "Tecnologia", subcategoria: "Wearables", imagem: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=200" },
    { id: 6, nome: "Console Game Station 5", sku: "GS5-CONSOLE", custoFornecedor: 3200.00, estoque: 15, categoria: "Games", subcategoria: "Consoles", imagem: "https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?w=200" },
  ];

  // Produtos em Tendência
  const [produtosTendencia, setProdutosTendencia] = useState([]);
  const [carregandoTendencias, setCarregandoTendencias] = useState(false);

  const carregarTendencias = async () => {
    setCarregandoTendencias(true);
    try {
      const resp = await authService.fetchAuth(`${BACKEND_URL}/api/tendencias/produtos?limit=10`);
      if (resp.ok) {
        const data = await resp.json();
        setProdutosTendencia(data.produtos || []);
      }
    } catch (error) {
      console.error("Erro ao carregar tendências:", error);
    } finally {
      setCarregandoTendencias(false);
    }
  };

  const vincularProdutoTendencia = async (produtoId) => {
    try {
      const resp = await authService.fetchAuth(`${BACKEND_URL}/afiliado/${USUARIO_ID}/vincular-produto`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ produto_id: produtoId }),
      });
      if (!resp.ok) throw new Error("Erro ao vincular produto.");
      toast.success("Produto vinculado! Você já pode divulgar.");
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    if (activeTab === 'tendencias') {
      carregarTendencias();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  // Marketplaces disponíveis (incluindo WhatsApp)
  const marketplaces = [
    { id: "mercado_livre", nome: "Mercado Livre", taxa: 16, logo: "🟡", custoPublicacao: 5 },
    { id: "shopee", nome: "Shopee", taxa: 14, logo: "🟠", custoPublicacao: 3 },
    { id: "amazon", nome: "Amazon", taxa: 15, logo: "📦", custoPublicacao: 5 },
    { id: "magalu", nome: "Magazine Luiza", taxa: 16, logo: "🔵", custoPublicacao: 4 },
    { id: "americanas", nome: "Americanas", taxa: 16, logo: "🔴", custoPublicacao: 4 },
    { id: "havan", nome: "Lojas Havan", taxa: 12, logo: "🏪", custoPublicacao: 3 },
    { id: "shopify", nome: "Shopify", taxa: 2, logo: "🟢", custoPublicacao: 2 },
    { id: "whatsapp", nome: "Loja WhatsApp", taxa: 0, logo: "💬", custoPublicacao: 1 },
  ];

  // Calcular preço de venda (SEM mostrar taxa ClickHub)
  const calcularPrecoVenda = (custoFornecedor, margemDesejada) => {
    // Taxa ClickHub é 2% mas NÃO mostramos ao usuário
    const taxaClickHubOculta = 2;
    const taxaMarketplace = 16; // média
    const taxaTotal = taxaMarketplace + taxaClickHubOculta;
    
    const precoVenda = custoFornecedor / (1 - (margemDesejada + taxaTotal) / 100);
    const taxaMP = precoVenda * taxaMarketplace / 100;
    const lucroLiquido = precoVenda - custoFornecedor - (precoVenda * taxaTotal / 100);
    
    return {
      precoVenda: precoVenda.toFixed(2),
      taxaMarketplace: taxaMP.toFixed(2),
      lucroLiquido: lucroLiquido.toFixed(2)
    };
  };

  // Toggle seleção de produto
  const toggleProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Toggle marketplace
  const toggleMarketplace = (mpId) => {
    setSelectedMarketplaces(prev => ({
      ...prev,
      [mpId]: !prev[mpId]
    }));
  };

  // Verificar se tem saldo suficiente
  const verificarSaldo = () => {
    const mpsAtivos = Object.entries(selectedMarketplaces).filter(([_, v]) => v);
    const custoTotal = mpsAtivos.reduce((sum, [mpId]) => {
      const mp = marketplaces.find(m => m.id === mpId);
      return sum + (mp?.custoPublicacao || 0) * selectedProducts.length;
    }, 0);
    return afiliadoData.saldoDisponivel >= custoTotal;
  };

  // Calcular custo total de publicação
  const calcularCustoPublicacao = () => {
    const mpsAtivos = Object.entries(selectedMarketplaces).filter(([_, v]) => v);
    return mpsAtivos.reduce((sum, [mpId]) => {
      const mp = marketplaces.find(m => m.id === mpId);
      return sum + (mp?.custoPublicacao || 0) * selectedProducts.length;
    }, 0);
  };

  // Iniciar postagem - CORRIGIDO para evitar erro de DOM
  const iniciarPostagem = async () => {
    if (!verificarSaldo()) {
      toast.error("⚠️ Saldo insuficiente! Adicione saldo na sua Carteira Digital.");
      return;
    }

    const custoTotal = calcularCustoPublicacao();
    setPostingProgress({ step: 0, total: 5, message: "Iniciando robô..." });
    setPostingComplete(false);
    
    const steps = [
      "🔍 Analisando produtos selecionados...",
      "📝 Gerando títulos otimizados com IA...",
      "🖼️ Processando imagens profissionais...",
      "📄 Criando descrições persuasivas...",
      "🚀 Publicando nos marketplaces..."
    ];

    for (let i = 0; i < steps.length; i++) {
      await new Promise(res => setTimeout(res, 1500));
      setPostingProgress({ step: i + 1, total: 5, message: steps[i] });
    }

    // Descontar saldo
    setAfiliadoData(prev => ({
      ...prev,
      saldoDisponivel: prev.saldoDisponivel - custoTotal,
      saldoBloqueado: prev.saldoBloqueado + custoTotal
    }));

    // Adicionar ao histórico
    setHistoricoCarteira(prev => [{
      id: Date.now(),
      tipo: 'saida',
      valor: custoTotal,
      descricao: `Publicação em ${Object.values(selectedMarketplaces).filter(Boolean).length} marketplace(s)`,
      data: new Date().toLocaleString('pt-BR')
    }, ...prev]);

    await new Promise(res => setTimeout(res, 1000));
    setPostingProgress({ step: 5, total: 5, message: "✅ Publicação concluída com sucesso!" });
    setPostingComplete(true);
    
    // Mostrar toast de sucesso em vez de alert
    toast.success(`🎉 Produtos publicados com sucesso! R$ ${custoTotal.toFixed(2)} debitados da sua carteira.`);
  };

  // Fechar modal de postagem de forma segura
  const fecharModalPostagem = () => {
    setPostingProgress(null);
    setPostingComplete(false);
    setSelectedProducts([]);
    setSelectedMarketplaces({});
    setShowPostModal(false);
  };

  // =====================
  // FUNÇÕES DA CARTEIRA DIGITAL - INTEGRADAS COM API PIX
  // =====================
  
  const handleDeposito = async () => {
    const valor = parseFloat(valorDeposito);
    if (isNaN(valor) || valor <= 0) {
      toast.error("Digite um valor válido para depósito");
      return;
    }
    
    if (valor < 10) {
      toast.error("Valor mínimo para depósito é R$ 10,00");
      return;
    }
    
    try {
      setPixLoading(true);
      
      // Criar pagamento PIX usando a API
      const result = await pixService.criarPagamento(
        USUARIO_ID, 
        'afiliado', 
        valor, 
        `Depósito ClickHub - ${afiliadoData.nome}`,
        afiliadoData.email
      );
      
      // Guardar dados do PIX
      setPixData({
        id: result.id,
        valor: result.valor,
        status: result.status,
        qrCode: result.qr_code,
        qrCodeBase64: result.qr_code_base64,
        copiaCola: result.copia_cola,
        expiracao: result.expiracao
      });
      
      // Fechar modal da carteira e abrir modal do PIX
      setShowCarteiraModal(false);
      setValorDeposito('');
      
      // Pequeno delay para evitar conflito de modais
      setTimeout(() => {
        setShowPixModal(true);
      }, 100);
      
    } catch (error) {
      toast.error(error.message || "Erro ao gerar PIX para depósito");
    } finally {
      setPixLoading(false);
    }
  };

  // Copiar código PIX
  const copiarCodigoPix = () => {
    if (pixData?.copiaCola) {
      navigator.clipboard.writeText(pixData.copiaCola);
      toast.success("📋 Código PIX copiado!");
    }
  };

  // Verificar status do pagamento PIX
  const verificarPagamentoPix = async () => {
    if (!pixData?.id) return;
    
    try {
      setPixLoading(true);
      const result = await pixService.verificarStatus(pixData.id);
      
      if (result.status === 'aprovado') {
        toast.success("✅ Pagamento confirmado! Saldo atualizado.");
        setShowPixModal(false);
        setPixData(null);
        await carregarCarteira();
      } else if (result.status === 'pendente') {
        toast.info("⏳ Pagamento ainda pendente. Aguarde a confirmação.");
      } else {
        toast.warning(`Status: ${result.status}`);
      }
    } catch (error) {
      toast.error("Erro ao verificar pagamento");
    } finally {
      setPixLoading(false);
    }
  };

  // Simular aprovação (modo sandbox)
  const simularAprovacaoPix = async () => {
    if (!pixData?.id) return;
    
    try {
      setPixLoading(true);
      const result = await pixService.simularAprovacao(pixData.id);
      
      if (result.success) {
        toast.success("✅ Pagamento aprovado (simulação)! Saldo atualizado.");
        setShowPixModal(false);
        setPixData(null);
        await carregarCarteira();
      }
    } catch (error) {
      toast.error(error.message || "Erro ao simular aprovação");
    } finally {
      setPixLoading(false);
    }
  };

  // Verificar se tem saldo para mostrar aviso
  const semSaldo = afiliadoData.saldoDisponivel <= 0;

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-950">
      <div className="flex-1 h-full overflow-y-auto">
        <div className="p-4 lg:p-6 min-h-full">
          <AvisoAutomacao24h usuarioId={USUARIO_ID} />
          {/* Header com Avatar e Carteira */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setShowPerfilModal(true)}
              >
                {afiliadoData.foto ? (
                  <img src={afiliadoData.foto} alt="Avatar" className="w-full h-full rounded-xl object-cover" />
                ) : (
                  <User className="w-6 h-6 text-white" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded">AFILIADO</span>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">{afiliadoData.nome}</h1>
                </div>
                <p className="text-gray-500 text-sm">Clique no avatar para editar perfil</p>
              </div>
            </div>

            {/* Carteira Digital */}
            <div className="flex items-center gap-2">
              <NotificationToggle usuarioId={USUARIO_ID} tipoUsuario="afiliado" variant="icon" />
              <Card 
                className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30 p-3 cursor-pointer hover:border-green-400 transition-all"
                onClick={() => setShowCarteiraModal(true)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Carteira Digital</p>
                    <p className="text-green-500 text-lg font-bold">R$ {afiliadoData.saldoDisponivel.toFixed(2)}</p>
                  </div>
                  <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-8">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            </div>
          </div>

          {/* Prompt de Notificações */}
          <NotificationPrompt usuarioId={USUARIO_ID} tipoUsuario="afiliado" />

          {/* Aviso de Saldo */}
          {semSaldo && (
            <Card className="bg-yellow-500/10 border-yellow-500/50 p-3 mb-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-400" />
                <div className="flex-1">
                  <p className="text-yellow-400 text-sm font-medium">
                    Adicione saldo na Carteira Digital para que o robo possa publicar seus produtos.
                  </p>
                </div>
                <Button size="sm" onClick={() => setShowCarteiraModal(true)} className="bg-orange-500 hover:bg-orange-600">
                  Adicionar Saldo
                </Button>
              </div>
            </Card>
          )}

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Card className="bg-gradient-to-br from-green-500 to-emerald-600 p-3 border-none">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-xs">Lucro Hoje</p>
                  <p className="text-white text-xl font-bold">R$ {afiliadoData.lucroHoje.toFixed(2)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-white/50" />
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 border-none">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-xs">Lucro Acumulado</p>
                  <p className="text-white text-xl font-bold">R$ {afiliadoData.lucroDisponivel.toFixed(2)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-white/50" />
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 p-3 border-none">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-xs">Produtos Ativos</p>
                  <p className="text-white text-xl font-bold">{afiliadoData.produtosAtivos}</p>
                </div>
                <Package className="w-8 h-8 text-white/50" />
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500 to-violet-600 p-3 border-none">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-xs">Vendas Mês</p>
                  <p className="text-white text-xl font-bold">{afiliadoData.vendasMes}</p>
                </div>
                <ShoppingCart className="w-8 h-8 text-white/50" />
              </div>
            </Card>
          </div>

          {/* Passo a Passo */}
          <Card className="bg-gradient-to-r from-orange-500/10 to-orange-600/10 border-orange-500/30 p-4 mb-4">
            <h3 className="text-gray-900 dark:text-white font-bold mb-3">🤖 Como Vender no ClickHub - Passo a Passo</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              <div className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg">
                <span className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">1</span>
                <span className="text-gray-300">Adicione saldo na Carteira</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg">
                <span className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">2</span>
                <span className="text-gray-300">Selecione o Estado (UF)</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg">
                <span className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">3</span>
                <span className="text-gray-300">Escolha o Fornecedor</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg">
                <span className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">4</span>
                <span className="text-gray-300">Selecione Produtos</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg">
                <span className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">5</span>
                <span className="text-gray-300">Ative o Robô!</span>
              </div>
            </div>
          </Card>

          {/* Tabs */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
            <Button
              onClick={() => { setActiveTab('fornecedores'); setSelectedFornecedor(null); setSelectedCategoria(null); }}
              className={`h-9 ${activeTab === 'fornecedores' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            >
              <Store className="w-4 h-4 mr-2" />
              Buscar Fornecedores
            </Button>
            <Button
              onClick={() => setActiveTab('catalogo')}
              disabled={!selectedFornecedor}
              className={`h-9 ${activeTab === 'catalogo' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            >
              <Layers className="w-4 h-4 mr-2" />
              Catálogo
            </Button>
            <Button
              onClick={() => setActiveTab('tendencias')}
              className={`h-9 ${activeTab === 'tendencias' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Tendências
            </Button>
            <Button
              onClick={() => setActiveTab('calculadora')}
              className={`h-9 ${activeTab === 'calculadora' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            >
              <Calculator className="w-4 h-4 mr-2" />
              Calculadora
            </Button>
          </div>

          {/* TAB: Buscar Fornecedores por Estado */}
          {activeTab === 'fornecedores' && (
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-orange-500" />
                Buscar Fornecedores por Estado
              </h2>

              {/* Filtro por Estado */}
              <div className="flex flex-col md:flex-row gap-3 mb-4">
                <div className="flex-1">
                  <label className="block text-sm text-gray-500 mb-1">Selecione o Estado (UF)</label>
                  <select
                    value={selectedEstado}
                    onChange={(e) => setSelectedEstado(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 h-10 text-white"
                  >
                    <option value="">Todos os Estados</option>
                    {estados.map(e => (
                      <option key={e.uf} value={e.uf}>{e.uf} - {e.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm text-gray-500 mb-1">Buscar por nome</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Nome do fornecedor..."
                      className="bg-gray-800 border-gray-700 h-10 pl-9"
                    />
                  </div>
                </div>
                <div className="flex items-end">
                  <Button onClick={carregarFornecedores} className="bg-orange-500 hover:bg-orange-600 h-10">
                    <Search className="w-4 h-4 mr-2" />
                    Buscar
                  </Button>
                </div>
              </div>

              {/* Lista de Fornecedores */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {fornecedoresFiltrados.map((fornecedor) => (
                  <Card 
                    key={fornecedor.id}
                    className="bg-gray-800/50 border-gray-700 p-4 hover:border-orange-500 transition-all cursor-pointer"
                    onClick={() => {
                      setSelectedFornecedor(fornecedor);
                      setActiveTab('catalogo');
                    }}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center text-2xl">
                        {fornecedor.logo}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-bold">{fornecedor.nome}</h3>
                        <div className="flex items-center gap-1 text-gray-400 text-sm">
                          <MapPin className="w-3 h-3" />
                          {fornecedor.cidade}, {fornecedor.estado}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                        {fornecedor.categoria}
                      </span>
                      <span className="flex items-center gap-1 text-yellow-400 text-xs">
                        <Star className="w-3 h-3 fill-current" />
                        {fornecedor.rating}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-400 mb-3">
                      <div className="flex items-center gap-1">
                        <Package className="w-3 h-3" />
                        {fornecedor.produtos} produtos
                      </div>
                      <div className="flex items-center gap-1">
                        <Truck className="w-3 h-3" />
                        {fornecedor.entregas}
                      </div>
                    </div>

                    <Button className="w-full bg-orange-500 hover:bg-orange-600 h-9">
                      <Eye className="w-4 h-4 mr-2" />
                      Ver Catálogo
                    </Button>
                  </Card>
                ))}

                {fornecedoresFiltrados.length === 0 && (
                  <div className="col-span-full text-center py-12">
                    <Store className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                    <h3 className="text-lg font-semibold text-white mb-2">Nenhum fornecedor encontrado</h3>
                    <p className="text-gray-400">Tente selecionar outro estado ou ajustar a busca</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB: Catálogo com Subcategorias */}
          {activeTab === 'catalogo' && selectedFornecedor && (
            <div>
              {/* Info do Fornecedor */}
              <Card className="bg-gray-800/50 border-gray-700 p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center text-2xl">
                      {selectedFornecedor.logo}
                    </div>
                    <div>
                      <h3 className="text-white font-bold">{selectedFornecedor.nome}</h3>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="flex items-center gap-1 text-gray-400">
                          <MapPin className="w-3 h-3" />
                          {selectedFornecedor.cidade}, {selectedFornecedor.estado}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="border-gray-700"
                    onClick={() => { setActiveTab('fornecedores'); setSelectedCategoria(null); setSelectedSubcategoria(null); }}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar
                  </Button>
                </div>
              </Card>

              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
                <span className="cursor-pointer hover:text-white" onClick={() => { setSelectedCategoria(null); setSelectedSubcategoria(null); }}>
                  Categorias
                </span>
                {selectedCategoria && (
                  <>
                    <ChevronRight className="w-4 h-4" />
                    <span className="cursor-pointer hover:text-white" onClick={() => setSelectedSubcategoria(null)}>
                      {selectedCategoria.nome}
                    </span>
                  </>
                )}
                {selectedSubcategoria && (
                  <>
                    <ChevronRight className="w-4 h-4" />
                    <span className="text-purple-400">{selectedSubcategoria.nome}</span>
                  </>
                )}
              </div>

              {/* Produtos Selecionados */}
              {selectedProducts.length > 0 && (
                <Card className="bg-green-500/10 border-green-500/30 p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckSquare className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-semibold">
                        {selectedProducts.length} produto(s) selecionado(s)
                      </span>
                    </div>
                    <Button
                      onClick={() => setShowPostModal(true)}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      <Bot className="w-4 h-4 mr-2" />
                      Ativar Robo
                    </Button>
                  </div>
                </Card>
              )}

              {/* Categorias (estilo Mercado Livre) */}
              {!selectedCategoria && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {categoriasCatalogo.map((cat) => (
                    <Card 
                      key={cat.id}
                      className="bg-gray-800/50 border-gray-700 p-4 hover:border-orange-500 transition-all cursor-pointer"
                      onClick={() => setSelectedCategoria(cat)}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-3xl">{cat.icone}</span>
                        <h3 className="text-white font-bold text-lg">{cat.nome}</h3>
                      </div>
                      <div className="space-y-1">
                        {cat.subcategorias.slice(0, 3).map((sub) => (
                          <p key={sub.id} className="text-gray-400 text-sm flex items-center gap-1">
                            <ChevronRight className="w-3 h-3" />
                            {sub.nome}
                          </p>
                        ))}
                        {cat.subcategorias.length > 3 && (
                          <p className="text-orange-400 text-sm">+ {cat.subcategorias.length - 3} mais</p>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Subcategorias */}
              {selectedCategoria && !selectedSubcategoria && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {selectedCategoria.subcategorias.map((sub) => (
                    <Card 
                      key={sub.id}
                      className="bg-gray-800/50 border-gray-700 p-3 hover:border-orange-500 transition-all cursor-pointer"
                      onClick={() => setSelectedSubcategoria(sub)}
                    >
                      <h4 className="text-white font-semibold mb-2">{sub.nome}</h4>
                      <p className="text-gray-400 text-xs">{sub.items.length} tipos de produtos</p>
                    </Card>
                  ))}
                </div>
              )}

              {/* Produtos */}
              {selectedSubcategoria && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {produtosCatalogo.map((produto) => {
                    const isSelected = selectedProducts.includes(produto.id);
                    const calc = calcularPrecoVenda(produto.custoFornecedor, margemPadrao);
                    return (
                      <Card 
                        key={produto.id}
                        className={`bg-gray-800/50 border-2 p-4 cursor-pointer transition-all ${
                          isSelected ? 'border-green-500 bg-green-500/10' : 'border-gray-700 hover:border-orange-500'
                        }`}
                        onClick={() => toggleProduct(produto.id)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className={`w-6 h-6 rounded flex items-center justify-center ${
                            isSelected ? 'bg-green-500' : 'bg-gray-700'
                          }`}>
                            {isSelected && <Check className="w-4 h-4 text-white" />}
                          </div>
                          <span className="text-xs text-gray-500">SKU: {produto.sku}</span>
                        </div>

                        <div className="w-full h-32 bg-gray-700 rounded-lg mb-3 overflow-hidden">
                          <img 
                            src={produto.imagem} 
                            alt={produto.nome}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <h4 className="text-white font-semibold text-sm mb-2 line-clamp-2">{produto.nome}</h4>

                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Custo:</span>
                            <span className="text-white">R$ {produto.custoFornecedor.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Sua Margem ({margemPadrao}%):</span>
                            <span className="text-green-400 font-bold">R$ {calc.lucroLiquido}</span>
                          </div>
                          <div className="flex justify-between border-t border-gray-700 pt-1">
                            <span className="text-gray-400">Preço de Venda:</span>
                            <span className="text-orange-400 font-bold">R$ {calc.precoVenda}</span>
                          </div>
                        </div>

                        <div className="mt-2 text-xs text-gray-500">
                          Estoque: {produto.estoque} unidades
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB: Tendências */}
          {activeTab === 'tendencias' && (
            <div>
              <Card className="bg-gradient-to-r from-orange-500/10 to-orange-600/10 border-orange-500/30 p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-gray-900 dark:text-white font-bold">🔥 Produtos em Alta no Catálogo</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      Ranking real dos produtos mais vendidos no ClickHub. Vincule para vender usando sua margem padrão de <strong className="text-orange-500">{margemPadrao}%</strong>
                    </p>
                  </div>
                </div>
              </Card>

              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-orange-500" />
                Mais Vendidos
              </h2>

              {carregandoTendencias ? (
                <div className="flex items-center justify-center py-16 text-gray-400">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  Carregando...
                </div>
              ) : produtosTendencia.length === 0 ? (
                <Card className="bg-gray-800/50 border-gray-700 p-8 text-center text-gray-400">
                  Nenhum produto com vendas registradas ainda.
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {produtosTendencia.map((produto) => {
                    const calc = calcularPrecoVenda(produto.preco, margemPadrao);
                    return (
                      <Card key={produto.produto_id} className="bg-gray-800/50 border-gray-700 p-4">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="text-white font-semibold">{produto.nome}</h4>
                          <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                            #{produto.rank}
                          </span>
                        </div>
                        <p className="text-gray-400 text-xs mb-2">{produto.categoria} • {produto.fornecedor}</p>

                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Vendas registradas:</span>
                            <span className="text-white">{produto.vendas}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Custo:</span>
                            <span className="text-white">R$ {(produto.preco || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Seu Lucro Est.:</span>
                            <span className="text-green-400 font-bold">R$ {calc.lucroLiquido}</span>
                          </div>
                        </div>

                        <Button
                          className="w-full mt-3 bg-orange-500 hover:bg-orange-600"
                          onClick={() => vincularProdutoTendencia(produto.produto_id)}
                        >
                          <Zap className="w-4 h-4 mr-2" />
                          Vincular Produto
                        </Button>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB: Calculadora */}
          {activeTab === 'calculadora' && (
            <div className="max-w-xl mx-auto">
              <Card className="bg-gray-800/50 border-gray-700 p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Calculator className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Calculadora de Lucro</h2>
                  <p className="text-gray-500 text-sm">Descubra quanto você vai ganhar!</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                      💰 Custo do Produto (R$)
                    </label>
                    <Input
                      type="number"
                      defaultValue={100}
                      className="bg-gray-800 border-gray-700 h-12 text-lg"
                      id="custoInput"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                      📈 Sua Margem de Lucro Desejada (%)
                    </label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={margemPadrao}
                        onChange={(e) => setMargemPadrao(Number(e.target.value))}
                        className="bg-gray-800 border-gray-700 h-12 text-lg flex-1"
                      />
                      <span className="text-2xl text-white font-bold">%</span>
                    </div>
                    <div className="flex gap-2 mt-2">
                      {[10, 20, 30, 40, 50].map(m => (
                        <Button
                          key={m}
                          size="sm"
                          onClick={() => setMargemPadrao(m)}
                          className={`h-7 ${margemPadrao === m ? 'bg-orange-500' : 'bg-gray-700'}`}
                        >
                          {m}%
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Resultado */}
                <div className="mt-6 p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl">
                  <h3 className="text-green-400 font-bold text-center mb-4">📊 RESULTADO</h3>
                  
                  {(() => {
                    const custo = 100;
                    const calc = calcularPrecoVenda(custo, margemPadrao);
                    return (
                      <>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="text-center p-3 bg-gray-800/50 rounded-lg">
                            <p className="text-gray-400 text-xs mb-1">Preço de Venda</p>
                            <p className="text-white text-2xl font-bold">R$ {calc.precoVenda}</p>
                          </div>
                          <div className="text-center p-3 bg-green-500/20 rounded-lg">
                            <p className="text-green-300 text-xs mb-1">SEU LUCRO LÍQUIDO</p>
                            <p className="text-green-400 text-2xl font-bold">R$ {calc.lucroLiquido}</p>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between text-gray-300">
                            <span>Custo do Produto:</span>
                            <span>R$ {custo.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-gray-300">
                            <span>Taxa Marketplace:</span>
                            <span className="text-red-400">- R$ {calc.taxaMarketplace}</span>
                          </div>
                          <div className="flex justify-between text-white font-bold pt-2 border-t border-gray-700">
                            <span>Sua Margem:</span>
                            <span className="text-orange-400">{margemPadrao}%</span>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <p className="text-center text-gray-500 text-xs mt-4">
                  💡 Esta é sua margem padrão para publicações automáticas
                </p>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Modal Carteira Digital - COMPLETO */}
      {showCarteiraModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
          <Card className="bg-gray-900 border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Wallet className="w-6 h-6 text-green-400" />
                  Carteira Digital
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCarteiraModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Saldos */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Card className="bg-green-500/10 border-green-500/30 p-3 text-center">
                  <p className="text-gray-400 text-xs">Saldo Disponível</p>
                  <p className="text-green-400 text-lg font-bold">R$ {afiliadoData.saldoDisponivel.toFixed(2)}</p>
                </Card>
                <Card className="bg-orange-500/10 border-orange-500/30 p-3 text-center">
                  <p className="text-gray-400 text-xs">Lucro Acumulado</p>
                  <p className="text-orange-400 text-lg font-bold">R$ {afiliadoData.lucroDisponivel.toFixed(2)}</p>
                </Card>
              </div>

              {/* Tabs da Carteira - Afiliado só pode Depositar */}
              <div className="flex gap-2 mb-4">
                <Button
                  size="sm"
                  onClick={() => setCarteiraTab('resumo')}
                  className={`flex-1 h-8 ${carteiraTab === 'resumo' ? 'bg-orange-500' : 'bg-gray-700'}`}
                >
                  <History className="w-4 h-4 mr-1" />
                  Histórico
                </Button>
                <Button
                  size="sm"
                  onClick={() => setCarteiraTab('depositar')}
                  className={`flex-1 h-8 ${carteiraTab === 'depositar' ? 'bg-green-500' : 'bg-gray-700'}`}
                >
                  <ArrowDownLeft className="w-4 h-4 mr-1" />
                  Depositar
                </Button>
              </div>

              {/* Aviso sobre carteira de depósito */}
              <Card className="bg-blue-500/10 border-blue-500/30 p-3 mb-4">
                <p className="text-blue-400 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Sua carteira é exclusiva para depósitos na ClickHub. Use o saldo para publicar produtos.
                </p>
              </Card>

              {/* Tab Resumo/Histórico */}
              {carteiraTab === 'resumo' && (
                <div>
                  <p className="text-sm text-gray-400 mb-2 flex items-center gap-1">
                    <History className="w-4 h-4" />
                    Histórico de Transações
                  </p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {historicoCarteira.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-gray-800/50 rounded-lg text-sm">
                        <div className="flex items-center gap-2">
                          {item.tipo === 'entrada' && <ArrowDownLeft className="w-4 h-4 text-green-400" />}
                          {item.tipo === 'saida' && <ArrowUpRight className="w-4 h-4 text-red-400" />}
                          {item.tipo === 'comissao' && <DollarSign className="w-4 h-4 text-green-400" />}
                          {item.tipo === 'saque' && <Banknote className="w-4 h-4 text-orange-400" />}
                          <div>
                            <p className="text-white">{item.descricao}</p>
                            <p className="text-gray-500 text-xs">{item.data}</p>
                          </div>
                        </div>
                        <span className={
                          item.tipo === 'entrada' || item.tipo === 'comissao' 
                            ? 'text-green-400' 
                            : item.tipo === 'saque' 
                              ? 'text-orange-400' 
                              : 'text-red-400'
                        }>
                          {item.tipo === 'entrada' || item.tipo === 'comissao' ? '+' : '-'} R$ {item.valor.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab Depositar */}
              {carteiraTab === 'depositar' && (
                <div>
                  <Card className="bg-green-500/10 border-green-500/30 p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <QrCode className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-semibold">Depósito via PIX</span>
                    </div>
                    <p className="text-gray-400 text-sm">
                      Adicione saldo para usar o robô e publicar produtos
                    </p>
                  </Card>

                  <div className="mb-4">
                    <label className="block text-sm text-gray-300 mb-2">Valor do Depósito</label>
                    <Input 
                      type="number"
                      placeholder="R$ 0,00" 
                      value={valorDeposito}
                      onChange={(e) => setValorDeposito(e.target.value)}
                      className="bg-gray-800 border-gray-700 h-12 text-lg" 
                    />
                    <div className="flex gap-2 mt-2">
                      {[50, 100, 200, 500].map(v => (
                        <Button 
                          key={v} 
                          size="sm" 
                          variant="outline" 
                          className="flex-1 border-gray-700 h-8 text-xs"
                          onClick={() => setValorDeposito(v.toString())}
                        >
                          R$ {v}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <Button 
                    className="w-full bg-green-500 hover:bg-green-600 h-12"
                    onClick={handleDeposito}
                    disabled={pixLoading || !valorDeposito}
                  >
                    {pixLoading ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <QrCode className="w-5 h-5 mr-2" />
                    )}
                    Gerar PIX para Depósito
                  </Button>
                  
                  <p className="text-gray-500 text-xs text-center mt-3">
                    Valor mínimo: R$ 10,00 • O saldo será creditado após confirmação do pagamento
                  </p>
                </div>
              )}

              {/* Tab Dados Bancários - Removido para Afiliado (não pode sacar) */}
            </div>
          </Card>
        </div>
      )}

      {/* Modal Perfil */}
      {showPerfilModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
          <Card className="bg-gray-900 border-gray-700 max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Meu Perfil</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowPerfilModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Foto */}
              <div className="text-center mb-4">
                <div
                  onClick={selecionarFoto}
                  className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-2 cursor-pointer hover:opacity-90 overflow-hidden"
                >
                  {afiliadoData.foto ? (
                    <img src={afiliadoData.foto} alt="Foto de perfil" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-10 h-10 text-white" />
                  )}
                </div>
                <input
                  ref={inputFotoRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={enviarFoto}
                  className="hidden"
                />
                <Button size="sm" variant="outline" className="border-gray-700 text-xs" onClick={selecionarFoto} disabled={enviandoFoto}>
                  <ImageIcon className="w-3 h-3 mr-1" />
                  {enviandoFoto ? "Enviando..." : "Alterar Foto"}
                </Button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Nome Completo</label>
                  <Input
                    value={afiliadoData.nome}
                    onChange={(e) => setAfiliadoData({...afiliadoData, nome: e.target.value})}
                    className="bg-gray-800 border-gray-700 h-9"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">E-mail</label>
                  <Input
                    value={afiliadoData.email}
                    onChange={(e) => setAfiliadoData({...afiliadoData, email: e.target.value})}
                    className="bg-gray-800 border-gray-700 h-9"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Telefone</label>
                  <Input
                    value={afiliadoData.telefone}
                    onChange={(e) => setAfiliadoData({...afiliadoData, telefone: e.target.value})}
                    className="bg-gray-800 border-gray-700 h-9"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Endereço</label>
                  <Input
                    value={afiliadoData.endereco}
                    onChange={(e) => setAfiliadoData({...afiliadoData, endereco: e.target.value})}
                    className="bg-gray-800 border-gray-700 h-9"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Margem Padrão de Lucro (%)</label>
                  <Input
                    type="number"
                    value={margemPadrao}
                    onChange={(e) => setMargemPadrao(Number(e.target.value))}
                    className="bg-gray-800 border-gray-700 h-9"
                  />
                </div>
              </div>

              <Button onClick={salvarPerfil} disabled={salvandoPerfil} className="w-full mt-4 bg-orange-500 hover:bg-orange-600">
                {salvandoPerfil ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Salvar Alterações
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal de Postagem - CORRIGIDO */}
      {showPostModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
          <Card className="bg-gray-900 border-gray-700 max-w-lg w-full p-6">
            {!postingProgress ? (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Bot className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">🤖 Ativar Robô</h2>
                  <p className="text-gray-500 text-sm">
                    Selecione os marketplaces para publicar {selectedProducts.length} produto(s)
                  </p>
                </div>

                {/* Saldo disponível */}
                <Card className="bg-green-500/10 border-green-500/30 p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Saldo disponível:</span>
                    <span className="text-green-400 font-bold">R$ {afiliadoData.saldoDisponivel.toFixed(2)}</span>
                  </div>
                </Card>

                {/* Lista de Marketplaces */}
                <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                  {marketplaces.map(mp => {
                    const custoTotal = mp.custoPublicacao * selectedProducts.length;
                    return (
                      <div 
                        key={mp.id}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                          selectedMarketplaces[mp.id] 
                            ? 'bg-green-500/20 border border-green-500' 
                            : 'bg-gray-800 border border-gray-700 hover:border-gray-600'
                        }`}
                        onClick={() => toggleMarketplace(mp.id)}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{mp.logo}</span>
                          <div>
                            <p className="text-white font-medium">{mp.nome}</p>
                            <p className="text-gray-400 text-xs">Taxa: {mp.taxa}% • Custo: R$ {custoTotal.toFixed(2)}</p>
                          </div>
                        </div>
                        <div className={`w-6 h-6 rounded flex items-center justify-center ${
                          selectedMarketplaces[mp.id] ? 'bg-green-500' : 'bg-gray-700'
                        }`}>
                          {selectedMarketplaces[mp.id] && <Check className="w-4 h-4 text-white" />}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Custo total */}
                <Card className="bg-gray-800/50 border-gray-700 p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Custo total da publicação:</span>
                    <span className="text-orange-400 font-bold">R$ {calcularCustoPublicacao().toFixed(2)}</span>
                  </div>
                </Card>

                {/* Botões */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => { setShowPostModal(false); setSelectedMarketplaces({}); }}
                    className="flex-1 border-gray-700"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={iniciarPostagem}
                    disabled={Object.values(selectedMarketplaces).filter(Boolean).length === 0}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Ativar Robo
                  </Button>
                </div>

                {!verificarSaldo() && (
                  <p className="text-yellow-400 text-xs text-center mt-2">
                    Saldo insuficiente — o robo vai solicitar deposito antes de publicar
                  </p>
                )}
              </>
            ) : postingComplete ? (
              /* Tela de sucesso - PRODUTO JA PUBLICADO PELO ROBO */
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-3 animate-pulse">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-lg font-bold text-green-400 mb-1">Produtos Publicados com Sucesso!</h2>
                <p className="text-white text-sm mb-1">O robô ClickHub já fez tudo por você:</p>
                
                <Card className="bg-green-500/10 border-green-500/30 p-3 mb-4 text-left">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-green-400" /><span className="text-gray-300">Titulo unico gerado</span></div>
                    <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-green-400" /><span className="text-gray-300">Descrição otimizada</span></div>
                    <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-green-400" /><span className="text-gray-300">Fotos configuradas</span></div>
                    <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-green-400" /><span className="text-gray-300">Preco e frete definidos</span></div>
                    <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-green-400" /><span className="text-gray-300">Categoria mapeada</span></div>
                    <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-green-400" /><span className="text-gray-300">Anúncio já está ATIVO</span></div>
                  </div>
                </Card>

                <p className="text-gray-500 text-xs mb-3">Seus produtos já estão à venda! Se quiser conferir, acesse seus painéis:</p>
                
                {/* Links OPCIONAIS para conferir */}
                <div className="space-y-1.5 mb-4 text-left">
                  {Object.entries(selectedMarketplaces).filter(([_, v]) => v).map(([mpId]) => {
                    const mp = marketplaces.find(m => m.id === mpId);
                    const mpLinks = {
                      mercado_livre: "https://www.mercadolivre.com.br/anuncie/bomni#menu-user",
                      shopee: "https://seller.shopee.com.br/portal/product/list/all",
                      amazon: "https://sellercentral.amazon.com.br/inventory",
                      magalu: "https://marketplace.magalu.com.br/produtos",
                      americanas: "https://marketplace.americanas.com.br/produtos",
                      havan: "https://www.havan.com.br/seller/products",
                      shopify: "https://admin.shopify.com/products",
                      casas_bahia: "https://marketplace.casasbahia.com.br/produtos",
                      shein: "https://seller.shein.com/product/list",
                      aliexpress: "https://seller.aliexpress.com/product/manage",
                      submarino: "https://marketplace.submarino.com.br/produtos",
                    };
                    return (
                      <a key={mpId} href={mpLinks[mpId] || "#"} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-gray-800/30 border border-gray-700/50 rounded-lg hover:border-green-500/30 transition-all group">
                        <div className="w-7 h-7 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="w-3.5 h-3.5 text-green-400" />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-white text-xs font-medium">{mp?.nome || mpId} <span className="text-green-400 text-[10px]">- Publicado</span></p>
                        </div>
                        <span className="text-gray-500 text-[10px] group-hover:text-orange-400 flex items-center gap-0.5">
                          Conferir <ExternalLink className="w-3 h-3" />
                        </span>
                      </a>
                    );
                  })}
                </div>
                
                <Button onClick={fecharModalPostagem} className="bg-green-500 hover:bg-green-600 w-full">
                  Pronto! Voltar ao Painel
                </Button>
              </div>
            ) : (
              /* Tela de progresso */
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <Bot className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">🤖 Robô Trabalhando...</h2>
                <p className="text-gray-400 mb-4">{postingProgress.message}</p>
                
                <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                  <div 
                    className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${(postingProgress.step / postingProgress.total) * 100}%` }}
                  />
                </div>
                <p className="text-gray-500 text-sm">{postingProgress.step} de {postingProgress.total}</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Modal PIX - QR Code para Depósito */}
      {showPixModal && pixData !== null && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center overflow-y-auto py-8 z-[60] p-4"
          onClick={(e) => e.target === e.currentTarget && (setShowPixModal(false), setPixData(null))}
        >
          <Card className="bg-gray-900 border-gray-700 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <QrCode className="w-6 h-6 text-green-400" />
                  Depósito PIX
                </h2>
                <Button variant="ghost" size="sm" onClick={() => { setShowPixModal(false); setPixData(null); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Valor do depósito */}
              <Card className="bg-green-500/10 border-green-500/30 p-4 mb-4 text-center">
                <p className="text-gray-400 text-sm">Valor do Depósito</p>
                <p className="text-green-400 text-3xl font-bold">R$ {pixData.valor?.toFixed(2)}</p>
                <p className="text-gray-500 text-xs mt-1">Expira em: {pixData.expiracao || '30 minutos'}</p>
              </Card>

              {/* QR Code ou Código */}
              <Card className="bg-gray-800/50 border-gray-700 p-4 mb-4">
                {pixData.qrCodeBase64 ? (
                  <div className="text-center">
                    <img 
                      src={pixData.qrCodeBase64.startsWith('data:') ? pixData.qrCodeBase64 : `data:image/png;base64,${pixData.qrCodeBase64}`} 
                      alt="QR Code PIX"
                      className="mx-auto mb-2 rounded-lg"
                      style={{ maxWidth: '200px' }}
                    />
                    <p className="text-green-400 text-sm font-medium">Escaneie o QR Code PIX acima</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-32 h-32 mx-auto bg-gray-700 rounded-lg flex items-center justify-center mb-2">
                      <QrCode className="w-16 h-16 text-gray-500" />
                    </div>
                    <p className="text-gray-400 text-sm">QR Code indisponível - Use o código Copia e Cola</p>
                  </div>
                )}
              </Card>

              {/* Código Copia e Cola */}
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">Código PIX Copia e Cola</label>
                <div className="flex gap-2">
                  <Input 
                    readOnly
                    value={pixData.copiaCola || ''}
                    className="bg-gray-800 border-gray-700 text-sm font-mono"
                  />
                  <Button 
                    onClick={copiarCodigoPix}
                    className="bg-green-500 hover:bg-green-600 px-4"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Instruções */}
              <Card className="bg-blue-500/10 border-blue-500/30 p-3 mb-4">
                <p className="text-blue-400 text-sm">
                  <strong>Como pagar:</strong>
                </p>
                <ol className="text-gray-400 text-xs mt-1 space-y-1 list-decimal list-inside">
                  <li>Abra o app do seu banco</li>
                  <li>Escolha pagar com PIX</li>
                  <li>Escaneie o QR Code ou cole o código</li>
                  <li>Confirme o pagamento</li>
                </ol>
              </Card>

              {/* Botões de ação */}
              <div className="space-y-2">
                <Button 
                  className="w-full bg-green-500 hover:bg-green-600"
                  onClick={verificarPagamentoPix}
                  disabled={pixLoading}
                >
                  {pixLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Verificar Pagamento
                </Button>
                
                {/* Botão de simulação (sandbox) - só aparece para admin,
                    já que o backend agora corretamente só permite admin
                    simular aprovação (achado de segurança corrigido:
                    antes qualquer afiliado podia aprovar o próprio
                    depósito sem pagar de verdade) */}
                {EH_ADMIN && (
                  <Button 
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    onClick={simularAprovacaoPix}
                    disabled={pixLoading}
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Simular Pagamento (Teste - Admin)
                  </Button>
                )}
                
                <p className="text-gray-500 text-xs text-center">
                  {EH_ADMIN
                    ? "⚠️ O botão \"Simular\" é apenas para testes. Em produção, o saldo será creditado automaticamente após o pagamento real."
                    : "Após pagar o PIX, o saldo é creditado automaticamente em alguns segundos. Você também pode clicar em \"Verificar Pagamento\"."}
                </p>
                {!EH_ADMIN && (
                  <p className="text-gray-600 text-[11px] text-center">
                    O dinheiro do depósito vai direto para a conta oficial da ClickHub - o saldo aqui é o controle do seu valor disponível para uso na plataforma.
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Afiliado;
